import { useRef, useCallback, useState } from 'react';

const UploadIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
    <polyline points="17,8 12,3 7,8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const FileIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
    <polyline points="14,2 14,8 20,8" />
  </svg>
);

const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
    <polyline points="20,6 9,17 4,12" />
  </svg>
);

const ACCEPTED_TYPES = '.jpeg,.jpg,.png,.webp,.pdf';
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
const MAX_FILES = 20;

function UploadPlansTab({
  files,
  onAddFiles,
  onRemoveFile,
  onClearFiles,
  isExtracting,
  onExtract,
  extractedProducts,
  onAddToStaging,
  onAddAllToStaging,
  onResolveProduct,
  onResolveGlobalProduct,
  onDismissCandidates,
  onAddToCatalog,
  error,
}) {
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [expandedPicker, setExpandedPicker] = useState(null);
  const [addingToCatalog, setAddingToCatalog] = useState(null); // product id being added

  const handleFiles = useCallback((fileList) => {
    const validFiles = Array.from(fileList).filter(file => {
      if (file.size > MAX_FILE_SIZE) {
        console.warn(`File ${file.name} exceeds 20MB limit`);
        return false;
      }
      const ext = file.name.toLowerCase().split('.').pop();
      if (!['jpeg', 'jpg', 'png', 'webp', 'pdf'].includes(ext)) {
        console.warn(`File ${file.name} has unsupported format`);
        return false;
      }
      return true;
    });

    if (files.length + validFiles.length > MAX_FILES) {
      const remaining = MAX_FILES - files.length;
      onAddFiles(validFiles.slice(0, Math.max(0, remaining)));
    } else {
      onAddFiles(validFiles);
    }
  }, [files.length, onAddFiles]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  // Count how many products need user resolution
  const needsResolution = extractedProducts.filter(p => p._status === 'pick' || p._status === 'global_pick').length;

  // For "Add All to Staging" — only add resolved/unmatched/global, not ones needing picks
  const addableProducts = extractedProducts.filter(p => p._status !== 'pick' && p._status !== 'global_pick');

  return (
    <div className="upload-plans-tab">
      {/* Drop Zone */}
      <div
        className={`upload-dropzone ${dragOver ? 'drag-over' : ''} ${files.length > 0 ? 'has-files' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPTED_TYPES}
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = '';
          }}
          style={{ display: 'none' }}
        />
        {files.length === 0 ? (
          <>
            <UploadIcon />
            <p className="upload-dropzone-text">
              Drop shelf photos, flyers, notes, or PDFs here
            </p>
            <p className="upload-dropzone-hint">
              or click to browse (max {MAX_FILES} files, {MAX_FILE_SIZE / (1024 * 1024)}MB each)
            </p>
          </>
        ) : (
          <p className="upload-dropzone-add">
            <PlusIcon /> Add more files
          </p>
        )}
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="upload-file-list">
          <div className="upload-file-list-header">
            <span>{files.length} file{files.length !== 1 ? 's' : ''}</span>
            <button className="upload-clear-btn" onClick={onClearFiles}>Clear</button>
          </div>
          {files.map((f) => (
            <div key={f.id} className={`upload-file-item ${f.status}`}>
              {f.preview ? (
                <img src={f.preview} alt={f.name} className="upload-file-thumb" />
              ) : (
                <div className="upload-file-icon"><FileIcon /></div>
              )}
              <div className="upload-file-info">
                <span className="upload-file-name">{f.name}</span>
                <span className="upload-file-size">{formatFileSize(f.size)}</span>
              </div>
              <button
                className="upload-file-remove"
                onClick={() => onRemoveFile(f.id)}
                title="Remove"
              >
                &times;
              </button>
            </div>
          ))}

          {/* Extract Button */}
          <button
            className="extract-btn"
            onClick={onExtract}
            disabled={isExtracting || files.length === 0}
          >
            {isExtracting ? (
              <>
                <span className="extract-spinner" />
                Extracting Products...
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                  <line x1="11" y1="8" x2="11" y2="14" />
                  <line x1="8" y1="11" x2="14" y2="11" />
                </svg>
                Extract Products
              </>
            )}
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="upload-error">
          <p>{error}</p>
        </div>
      )}

      {/* Extraction Results */}
      {extractedProducts.length > 0 && (
        <div className="extraction-results">
          <div className="extraction-results-header">
            <span>
              {extractedProducts.length} product{extractedProducts.length !== 1 ? 's' : ''} found
              {needsResolution > 0 && (
                <span className="needs-resolution-badge">{needsResolution} need matching</span>
              )}
            </span>
            <button
              className="add-all-btn"
              onClick={() => onAddAllToStaging(addableProducts)}
              disabled={addableProducts.length === 0}
            >
              Add All to Staging
            </button>
          </div>
          <div className="extraction-results-list">
            {extractedProducts.map((product, index) => {
              const productId = product.id || product.name;
              const name = product.product_name || product.name || 'Unknown';
              const brand = product.brand || '';
              // Extracted prices from the photo (source of truth)
              const salePrice = product.sale_price;
              const regularPrice = product.regular_price;
              // Fallback to matched product prices
              const displaySalePrice = salePrice ?? product.promo_price ?? product.promoPrice;
              const displayRegularPrice = regularPrice ?? product.original_price ?? product.originalPrice;
              const confidence = product.confidence;
              const status = product._status;
              const candidates = status === 'global_pick'
                ? (product.global_candidates || [])
                : (product.candidates || []);
              const isPickerOpen = expandedPicker === productId;
              const imgSrc = product.cleaned_img_large_src || product.original_img_large_src || product.image_url;
              const packSize = product.quantity || product.pack_size;

              return (
                <div key={productId || index} className={`extracted-product-card ${status}`}>
                  <div className="extracted-product-row">
                    {/* Product image thumbnail */}
                    {imgSrc ? (
                      <img src={imgSrc} alt="" className="extracted-product-thumb" />
                    ) : (
                      <div className="extracted-product-thumb-placeholder">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5">
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <polyline points="21,15 16,10 5,21" />
                        </svg>
                      </div>
                    )}
                    <div className="extracted-product-info">
                      <span className="extracted-product-name">{name}</span>
                      {brand && <span className="extracted-product-brand">{brand}</span>}
                      <div className="extracted-product-meta">
                        {/* Prices extracted from photo */}
                        {displaySalePrice != null && (
                          <span className="extracted-product-price">${displaySalePrice}</span>
                        )}
                        {displayRegularPrice != null && displaySalePrice != null && displayRegularPrice !== displaySalePrice && (
                          <span className="extracted-product-reg-price">${displayRegularPrice}</span>
                        )}
                        {displayRegularPrice != null && displaySalePrice == null && (
                          <span className="extracted-product-price">${displayRegularPrice}</span>
                        )}
                        {packSize && <span className="extracted-product-pack">{packSize}</span>}
                      </div>
                      <div className="extracted-product-status">
                        {status === 'resolved' && (
                          <span className="extracted-product-matched">
                            <CheckIcon /> {product._addedToCatalog ? 'Added to catalog' : 'Matched'}
                          </span>
                        )}
                        {status === 'global' && (
                          <span className="extracted-product-global">
                            Found in library
                          </span>
                        )}
                        {(status === 'pick' || status === 'global_pick') && (
                          <button
                            className={`extracted-product-pick-btn ${status === 'global_pick' ? 'global' : ''}`}
                            onClick={() => setExpandedPicker(isPickerOpen ? null : productId)}
                          >
                            {candidates.length} {status === 'global_pick' ? 'library' : ''} matches — pick one
                          </button>
                        )}
                        {status === 'unmatched' && (
                          <span className="extracted-product-unmatched">New product</span>
                        )}
                        {confidence != null && confidence > 0 && (status === 'resolved' || status === 'global') && (
                          <span className={`extracted-product-confidence ${confidence >= 0.8 ? 'high' : confidence >= 0.5 ? 'medium' : 'low'}`}>
                            {Math.round(confidence * 100)}%
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="extracted-product-actions">
                      {/* Add to catalog button for global matches */}
                      {status === 'global' && onAddToCatalog && (
                        <button
                          className="add-to-catalog-btn"
                          onClick={async () => {
                            setAddingToCatalog(productId);
                            try {
                              await onAddToCatalog(productId);
                            } catch (e) { /* handled in hook */ }
                            setAddingToCatalog(null);
                          }}
                          disabled={addingToCatalog === productId}
                          title="Add to retailer catalog"
                        >
                          {addingToCatalog === productId ? '...' : '+ Catalog'}
                        </button>
                      )}
                      {status !== 'pick' && status !== 'global_pick' && (
                        <button
                          className="add-to-staging-btn"
                          onClick={() => onAddToStaging(product)}
                          title="Add to staging"
                        >
                          <PlusIcon />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Candidate Picker - inline dropdown */}
                  {(status === 'pick' || status === 'global_pick') && isPickerOpen && (
                    <div className="candidate-picker">
                      <div className={`candidate-picker-header ${status === 'global_pick' ? 'global' : ''}`}>
                        <span>
                          {status === 'global_pick' ? 'Select from library' : 'Select the correct product'} for "{product.name}"
                        </span>
                        <button
                          className="candidate-dismiss-btn"
                          onClick={() => {
                            onDismissCandidates(productId);
                            setExpandedPicker(null);
                          }}
                          title="Skip — use extracted data"
                        >
                          Skip
                        </button>
                      </div>
                      <div className="candidate-list">
                        {candidates.map((candidate, ci) => {
                          const cImg = candidate.custom_image_url || candidate.original_img_large_src || candidate.img_large_src || candidate.image_url;
                          return (
                            <button
                              key={candidate.retailer_sku_id || candidate._id || ci}
                              className={`candidate-item ${status === 'global_pick' ? 'global' : ''}`}
                              onClick={() => {
                                if (status === 'global_pick') {
                                  onResolveGlobalProduct(productId, candidate);
                                } else {
                                  onResolveProduct(productId, candidate);
                                }
                                setExpandedPicker(null);
                              }}
                            >
                              {cImg ? (
                                <img src={cImg} alt="" className="candidate-thumb" />
                              ) : (
                                <div className="candidate-thumb-placeholder" />
                              )}
                              <div className="candidate-info">
                                <span className="candidate-name">{candidate.product_name || candidate.name}</span>
                                <span className="candidate-detail">
                                  {candidate.brand && <span>{candidate.brand}</span>}
                                  {candidate.promo_price && <span className="candidate-price">${candidate.promo_price}</span>}
                                  {candidate.original_price && <span className="candidate-orig-price">${candidate.original_price}</span>}
                                  {candidate.retailer_sku_id && <span className="candidate-sku">SKU: {candidate.retailer_sku_id}</span>}
                                  {candidate.package_size && <span className="candidate-sku">{candidate.package_size}</span>}
                                </span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default UploadPlansTab;
